import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight } from "lucide-react";

import { listPublishedPosts } from "@/lib/blog.functions";

const postsQuery = queryOptions({
  queryKey: ["blog", "published"],
  queryFn: () => listPublishedPosts(),
});

export const Route = createFileRoute("/blog")({
  loader: ({ context }) => context.queryClient.ensureQueryData(postsQuery),
  head: () => ({
    meta: [
      { title: "Blog H2J | Vendas, tráfego pago, marketing e IA para empresas" },
      {
        name: "description",
        content:
          "Conteúdos práticos sobre vendas, tráfego pago, social media, sites, Google Meu Negócio e inteligência artificial para empresas que querem crescer com consistência.",
      },
      { property: "og:title", content: "Blog H2J | Vendas, tráfego pago, marketing e IA" },
      {
        property: "og:description",
        content:
          "Conteúdos práticos sobre vendas, tráfego pago, social media, sites, Google Meu Negócio e inteligência artificial para empresas que querem crescer com consistência.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://h2jcompany.com.br/blog" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://h2jcompany.com.br/blog" }],
  }),
  component: BlogPage,
});

function formatDate(iso: string | null) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function BlogPage() {
  const { data: posts } = useSuspenseQuery(postsQuery);

  return (
    <main className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/70">
        <div className="section-shell flex h-18 items-center justify-between">
          <Link to="/" aria-label="H2J Growth Company">
            <img
              src="/h2j-logo-340.webp"
              alt="H2J Growth Company"
              width={340}
              height={96}
              className="h-10 w-auto"
            />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:text-accent-ink"
          >
            <ArrowLeft className="size-4" /> Voltar ao site
          </Link>
        </div>
      </header>

      <section className="section-shell py-16 sm:py-24">
        <p className="eyebrow">Blog H2J</p>
        <h1 className="mt-4 max-w-3xl text-4xl font-semibold leading-[1.1] sm:text-5xl">
          Estratégia, performance e crescimento.
        </h1>
        <p className="mt-5 max-w-xl text-muted-foreground">
          Conteúdos práticos sobre marketing, vendas e tecnologia para empresas que querem crescer
          com consistência.
        </p>

        {posts.length === 0 ? (
          <p className="mt-16 border-t-2 border-primary bg-surface-soft p-8 text-muted-foreground">
            Em breve, novos conteúdos por aqui.
          </p>
        ) : (
          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {posts.map((post) => (
              <Link
                key={post.id}
                to="/blog/$slug"
                params={{ slug: post.slug }}
                className="group flex flex-col border border-border bg-background transition-colors hover:border-primary"
              >
                <span aria-hidden className="h-1 w-full bg-primary/70 group-hover:bg-primary" />
                {post.cover_image_url && (
                  <img
                    src={post.cover_image_url}
                    alt=""
                    loading="lazy"
                    className="aspect-[16/9] w-full object-cover"
                  />
                )}
                <div className="flex flex-1 flex-col p-6">
                  <p className="text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    {formatDate(post.published_at)}
                  </p>
                  <h2 className="mt-3 text-xl font-semibold leading-snug text-foreground transition-colors group-hover:text-accent-ink">
                    {post.title}
                  </h2>
                  {post.excerpt && (
                    <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                      {post.excerpt}
                    </p>
                  )}
                  <span className="mt-5 inline-flex items-center gap-2 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-primary">
                    Ler artigo <ArrowRight className="size-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
